import { useCallback } from "react";
import ResponseError from "./ResponseError";
import { parseResponseBody } from "./parseResponseBody";

/**
 * usePublicFetch
 * - For unauthenticated/public endpoints (no cookies by default)
 * - Adds timeout + robust error handling
 *
 * @param {Object} defaults
 * @param {string} [defaults.baseUrl=""]     Optional base URL prefix
 * @param {Object} [defaults.headers]        Default headers to merge
 * @param {number} [defaults.timeout=10000]  Default timeout (ms)
 * @param {"omit"|"same-origin"|"include"} [defaults.credentials="omit"]
 *
 * @returns {(pathOrUrl: string, options?: RequestInit & {timeout?: number}) => Promise<any>}
 */
export default function usePublicFetch(defaults = {}) {
  const {
    baseUrl = "",
    headers: defaultHeaders = {},
    timeout: defaultTimeout = 10_000,
    credentials: defaultCredentials = "omit",
  } = defaults;

  const publicFetch = useCallback(
    async (pathOrUrl, options = {}) => {
      const {
        timeout = defaultTimeout,
        signal: externalSignal,
        headers,
        credentials,
        ...rest
      } = options;

      // Resolve absolute URL
      const url = /^https?:\/\//i.test(pathOrUrl)
        ? pathOrUrl
        : `${baseUrl}${pathOrUrl}`;

      // Merge headers
      const mergedHeaders = { ...defaultHeaders, ...headers };

      // Setup abort/timeout
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeout);

      // Bridge an external signal if provided
      if (externalSignal) {
        if (externalSignal.aborted) {
          controller.abort(externalSignal.reason);
        } else {
          // Abort when the external signal aborts
          externalSignal.addEventListener(
            "abort",
            () => controller.abort(externalSignal.reason),
            { once: true }
          );
        }
      }

      try {
        const res = await fetch(url, {
          ...rest,
          headers: mergedHeaders,
          credentials: credentials ?? defaultCredentials,
          signal: controller.signal,
        });

        const data = await parseResponseBody(res);

        const out = {
          success: res.ok,
          status: res.status,
          statusText: res.statusText,
          url: res.url,
          headers: Object.fromEntries(res.headers.entries()),
          data: data?.data ?? data ?? {},
          meta: data?.meta ?? {},
          // data, // parsed JSON (or null)
          // // text: raw,
        };

        if (!res.ok) {
          // Server returned an error status — throw with rich context
          throw new ResponseError(
            data?.message || res.statusText || "Request failed",
            out
          );
        }

        // replaced this with above
        //Problem is HERE, throwing this error hides the original JSON coming back
        // if (!res.ok) {
        //   //message from server
        //   throw new ResponseError(data?.message ?? res.message, {
        //     //}`HTTP ${res.status}`, {
        //     response: res,
        //     data,
        //   });
        // }

        // return data;
        return out; // success: { success:true, status, data, ... }
      } catch (err) {
        // Network error/timeouts/etc. — wrap so the UI can handle uniformly
        if (err instanceof ResponseError) throw err;

        if (err?.name === "AbortError") {
          throw new ResponseError("Request timed out or aborted", {
            cause: err,
          });
        }

        // Network failures (DNS/CORS/offline) are usually TypeError
        if (err instanceof TypeError) {
          throw new ResponseError("Network error", { cause: err });
        }

        throw err; // already a ResponseError or unexpected
      } finally {
        clearTimeout(timer);
      }
    },
    [] //[baseUrl, defaultHeaders, defaultTimeout, defaultCredentials]
  );

  return publicFetch; // usage: const publicFetch = usePublicFetch();
}
